import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Plug, Plus, Copy, Trash2, AlertTriangle, Check, ShieldCheck, Terminal, Code2,
} from "lucide-react";

export default function McpAccess() {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newTokenName, setNewTokenName] = useState("");
  const [justCreated, setJustCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState(null);

  const mcpUrl = `${window.location.origin}/api/mcp`;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api-tokens");
      setTokens(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e?.preventDefault?.();
    const name = newTokenName.trim();
    if (name.length < 1) return;
    setCreating(true);
    try {
      const { data } = await api.post("/api-tokens", { name });
      setJustCreated(data);
      setNewTokenName("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this token? Any client using it will stop working immediately.")) return;
    setRevoking(id);
    try {
      await api.delete(`/api-tokens/${id}`);
      toast.success("Token revoked");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setRevoking(null);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  const claudeDesktopConfig = justCreated
    ? JSON.stringify({
        mcpServers: {
          "scalater-crm": {
            type: "http",
            url: mcpUrl,
            headers: { Authorization: `Bearer ${justCreated.token}` },
          },
        },
      }, null, 2)
    : null;

  return (
    <div className="px-8 py-10 max-w-5xl" data-testid="mcp-access-page">
      <header className="mb-10">
        <div className="overline mb-2 text-[#002FA7]">Developer</div>
        <h1 className="font-heading text-4xl font-black tracking-tight mb-2">
          MCP &amp; API access
        </h1>
        <p className="text-zinc-600 max-w-2xl">
          Plug Scalater CRM into Claude Desktop, Cursor, n8n, or any other
          Model Context Protocol client. Issue long-lived API tokens here, then
          paste them into your client's config to control the entire CRM with
          natural language.
        </p>
      </header>

      {/* Endpoint box */}
      <section className="border border-zinc-200 bg-white p-6 mb-8" data-testid="mcp-endpoint-box">
        <div className="flex items-center gap-2 mb-3">
          <Plug className="w-4 h-4 text-[#002FA7]" />
          <h2 className="font-heading text-sm font-bold uppercase tracking-wider">
            MCP Server endpoint
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <code className="flex-1 font-mono text-sm bg-zinc-100 px-4 py-3 border border-zinc-200 break-all" data-testid="mcp-endpoint-url">
            {mcpUrl}
          </code>
          <button
            onClick={() => copyToClipboard(mcpUrl)}
            data-testid="mcp-endpoint-copy"
            className="px-4 py-3 border border-zinc-300 hover:bg-zinc-100 transition-colors text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
            Copy
          </button>
        </div>
        <p className="text-xs text-zinc-500 mt-3 font-mono">
          transport: streamable-http &middot; protocol: 2025-06-18 &middot; 10 tools
        </p>
      </section>

      {/* New token form OR "just created" reveal */}
      {!justCreated ? (
        <section className="border border-zinc-200 bg-white p-6 mb-8">
          <h2 className="font-heading text-sm font-bold uppercase tracking-wider mb-1">
            Generate new token
          </h2>
          <p className="text-xs text-zinc-500 mb-4">
            Tokens are workspace-scoped and never expire (revoke from here when done).
          </p>
          <form onSubmit={create} className="flex gap-3 flex-wrap">
            <input
              value={newTokenName}
              onChange={(e) => setNewTokenName(e.target.value)}
              placeholder="e.g. Claude Desktop · MacBook"
              maxLength={80}
              data-testid="mcp-new-token-name"
              className="flex-1 min-w-[260px] border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900"
            />
            <button
              type="submit"
              disabled={creating || newTokenName.trim().length < 1}
              data-testid="mcp-create-token-btn"
              className="inline-flex items-center gap-2 px-5 py-2 bg-[#002FA7] text-white font-semibold uppercase tracking-wider text-xs hover:bg-[#001f7f] transition-colors disabled:opacity-50"
            >
              <Plus className="w-4 h-4" /> {creating ? "Creating…" : "Create token"}
            </button>
          </form>
        </section>
      ) : (
        <section className="border-2 border-amber-300 bg-amber-50 p-6 mb-8" data-testid="mcp-just-created">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
            <div>
              <h2 className="font-heading text-lg font-bold mb-1">
                Copy this token now
              </h2>
              <p className="text-sm text-amber-900">
                This is the <strong>only time</strong> you will see the full token. Store it
                somewhere safe before leaving this page.
              </p>
            </div>
          </div>
          <div className="bg-zinc-900 text-emerald-300 font-mono text-sm p-4 break-all relative" data-testid="mcp-token-reveal">
            {justCreated.token}
            <button
              onClick={() => copyToClipboard(justCreated.token)}
              data-testid="mcp-token-copy"
              className="absolute top-2 right-2 p-2 hover:bg-zinc-800 transition-colors"
              title="Copy token"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>

          {/* Claude Desktop config */}
          <details className="mt-5" open>
            <summary className="cursor-pointer text-sm font-bold text-zinc-900 inline-flex items-center gap-2" data-testid="mcp-claude-config-toggle">
              <Code2 className="w-4 h-4" /> Claude Desktop config snippet
            </summary>
            <p className="text-xs text-zinc-700 mt-2 mb-2">
              Open Claude Desktop &rarr; Settings &rarr; Connectors &rarr; Edit Config and merge:
            </p>
            <div className="bg-zinc-900 text-zinc-100 font-mono text-xs p-4 relative whitespace-pre-wrap overflow-x-auto" data-testid="mcp-claude-config">
              {claudeDesktopConfig}
              <button
                onClick={() => copyToClipboard(claudeDesktopConfig)}
                className="absolute top-2 right-2 p-2 hover:bg-zinc-800 transition-colors"
                title="Copy snippet"
                data-testid="mcp-claude-config-copy"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </details>

          <button
            onClick={() => setJustCreated(null)}
            data-testid="mcp-dismiss-reveal"
            className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white font-semibold uppercase tracking-wider text-xs hover:bg-zinc-800 transition-colors"
          >
            <ShieldCheck className="w-4 h-4" /> I have saved my token
          </button>
        </section>
      )}

      {/* Existing tokens table */}
      <section className="border border-zinc-200 bg-white" data-testid="mcp-tokens-list">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <h2 className="font-heading text-sm font-bold uppercase tracking-wider">
            Active tokens ({tokens.filter((t) => !t.revoked_at).length})
          </h2>
          {loading && <span className="text-xs text-zinc-500">Loading…</span>}
        </div>
        {tokens.length === 0 && !loading ? (
          <div className="px-6 py-12 text-center text-zinc-500 text-sm">
            No tokens yet. Create one above to connect Claude Desktop or any other MCP client.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="text-left px-6 py-3 font-semibold text-xs uppercase tracking-wider text-zinc-700">Name</th>
                <th className="text-left px-6 py-3 font-semibold text-xs uppercase tracking-wider text-zinc-700">Prefix</th>
                <th className="text-left px-6 py-3 font-semibold text-xs uppercase tracking-wider text-zinc-700">Created</th>
                <th className="text-left px-6 py-3 font-semibold text-xs uppercase tracking-wider text-zinc-700">Last used</th>
                <th className="text-left px-6 py-3 font-semibold text-xs uppercase tracking-wider text-zinc-700">Status</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => (
                <tr key={t.id} className="border-b border-zinc-100" data-testid={`mcp-token-row-${t.id}`}>
                  <td className="px-6 py-3 font-semibold text-zinc-900">{t.name}</td>
                  <td className="px-6 py-3 font-mono text-zinc-700">crm_{t.token_prefix}_…</td>
                  <td className="px-6 py-3 text-zinc-600 text-xs">{formatDate(t.created_at)}</td>
                  <td className="px-6 py-3 text-zinc-600 text-xs">{t.last_used_at ? formatDate(t.last_used_at) : "—"}</td>
                  <td className="px-6 py-3">
                    {t.revoked_at ? (
                      <span className="inline-block px-2 py-0.5 bg-zinc-100 text-zinc-600 text-xs font-mono">REVOKED</span>
                    ) : (
                      <span className="inline-block px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-mono">ACTIVE</span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-right">
                    {!t.revoked_at && (
                      <button
                        onClick={() => revoke(t.id)}
                        disabled={revoking === t.id}
                        data-testid={`mcp-revoke-${t.id}`}
                        className="inline-flex items-center gap-1 text-xs text-red-700 hover:text-red-900 font-semibold uppercase tracking-wider disabled:opacity-50"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        {revoking === t.id ? "…" : "Revoke"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Curl example */}
      <section className="mt-10 border border-zinc-200 bg-white p-6" data-testid="mcp-curl-example">
        <div className="flex items-center gap-2 mb-3">
          <Terminal className="w-4 h-4 text-[#002FA7]" />
          <h2 className="font-heading text-sm font-bold uppercase tracking-wider">
            Quick test with curl
          </h2>
        </div>
        <pre className="bg-zinc-900 text-zinc-100 font-mono text-xs p-4 overflow-x-auto whitespace-pre">
{`# 1. List available tools
curl -X POST ${mcpUrl} \\
  -H "Authorization: Bearer <YOUR_TOKEN>" \\
  -H "Content-Type: application/json" \\
  -d '{"jsonrpc":"2.0","id":1,"method":"tools/list","params":{}}'

# 2. Pipeline summary
curl -X POST ${mcpUrl} \\
  -H "Authorization: Bearer <YOUR_TOKEN>" \\
  -H "Content-Type: application/json" \\
  -d '{"jsonrpc":"2.0","id":2,"method":"tools/call",
       "params":{"name":"pipeline_summary","arguments":{}}}'`}
        </pre>
      </section>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
